import { Button, Group, Modal, Select } from '@common/ui'
import { com } from '@eidu/web'
import { useQuery } from '@tanstack/react-query'
import { getKnownKindKey, kindDefinitions } from '../../../../domain/kindRegistry'
import listVersions from '../../../../io/api/entity/data/listVersions'
import useEntityById from '../../../../io/useEntityById'
import useRegistryAsset from '../../../../io/useRegistryAsset'
import EntityPickerWithRegistry from './EntityPickerWithRegistry'
import EntityId = com.eidu.entity.EntityId
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind

const isAssetKind = (kind: EntityTypeKind) => {
  const key = getKnownKindKey(kind)
  return key !== undefined && kindDefinitions[key].sidebarGroup === 'asset'
}

type AssetReferencePickerProps = {
  open: boolean
  value: EntityId | undefined
  versionId?: string
  onChange: (entityId: EntityId | undefined) => void
  onVersionChange?: (versionId: string | undefined) => void
  onConfirm: () => void
  onCancel: () => void
}

const formatDate = (ms: number): string => new Date(ms).toLocaleString()

const AssetReferencePicker = ({
  open,
  value,
  versionId,
  onChange,
  onVersionChange,
  onConfirm,
  onCancel,
}: AssetReferencePickerProps) => {
  const { entity: localEntity, loading: localLoading } = useEntityById(value)
  const valueOnlyInRegistry = !!value && !localLoading && localEntity === null
  const { detail: registryDetail, isLoading: registryLoading } = useRegistryAsset(
    valueOnlyInRegistry ? value!.toString() : undefined
  )
  const valueSource: 'org' | 'registry' | undefined = !value
    ? undefined
    : localEntity
      ? 'org'
      : registryDetail
        ? 'registry'
        : undefined
  const resolvingValueSource = !!value && (localLoading || (valueOnlyInRegistry && registryLoading))

  const localVersionsQuery = useQuery({
    queryKey: ['versions', value?.toString()],
    queryFn: () => listVersions({ id: value! }),
    enabled: !!value && !!localEntity,
  })
  const versions = valueOnlyInRegistry ? registryDetail?.versions ?? [] : localVersionsQuery.data ?? []

  const versionPin = versions.length > 0 && (
    <Select
      label="Pin to version"
      value={versionId ?? ''}
      onChange={(next) => onVersionChange?.(!next ? undefined : next)}
      allowDeselect={false}
      data={[
        { value: '', label: 'Latest (unversioned)' },
        ...versions.map((record) => ({
          value: record.versionId,
          label: `${record.versionId.slice(0, 8)}… · ${formatDate(record.createdAt)}`,
        })),
      ]}
    />
  )

  return (
    <Modal opened={open} onClose={onCancel} title="Insert Asset Reference" size="lg">
      <EntityPickerWithRegistry
        value={value}
        onChange={onChange}
        kindFilter={isAssetKind}
        selectedVersionId={versionId}
        onRegistryVersionSelect={onVersionChange}
        localPickerExtras={versionPin}
        valueSource={valueSource}
        resolvingValueSource={resolvingValueSource}
      />
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={!value}>
          Insert
        </Button>
      </Group>
    </Modal>
  )
}

export default AssetReferencePicker
